import { useEffect, useMemo, useRef, useState } from 'react';
import { db } from '../../firebase/firebaseConfig';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { toMs } from './usageTiers';

// Support-message and admin-note activity per account, for the message lane of
// the Accounts tab's History timeline.
//
// Same shape and reasoning as useAccountComments: one small read per displayed
// account, bounded concurrency, only while the view that needs it is on screen.
// We fetch whole (small) subcollections rather than a date-bounded slice on
// purpose — the History window is a client-side control the user changes
// constantly, and re-querying Firestore on every range change would be far
// worse than holding a few dozen timestamps per account.
//
// If this per-account fan-out ever becomes slow at fleet size, the drop-in
// replacement is one admin endpoint returning the same map, built server-side
// with the Admin SDK. Don't reach for that until it's actually needed.

const CONCURRENCY = 25;
// Notes are stored as revisions of several kinds; only comment revisions are
// admin activity worth marking on the timeline.
const COMMENT_KIND = 'comment';

/** Run `worker` over `items` with a bounded number in flight. */
async function mapLimit(items, limit, worker) {
  const results = new Array(items.length);
  let cursor = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const i = cursor++;
      results[i] = await worker(items[i], i);
    }
  });
  await Promise.all(runners);
  return results;
}

/**
 * Message + note timestamps for one account, oldest first.
 * Returns { messages: [{ ms, from }], notes: [{ ms, adminEmail }] }.
 */
async function fetchActivity(accountUUID) {
  // A LONE equality filter is served by Firestore's automatic single-field
  // index; adding an orderBy would make it composite and need an index deploy.
  // Both collections are small per account, so we sort client-side.
  const [messagesSnap, notesSnap] = await Promise.all([
    getDocs(collection(db, 'supportConversations', accountUUID, 'messages')),
    getDocs(query(collection(db, 'accountNotes', accountUUID, 'notes'), where('kind', '==', COMMENT_KIND))),
  ]);

  const messages = messagesSnap.docs
    .map((d) => {
      const data = d.data();
      return {
        ms: toMs(data.createdAt),
        // Anything that isn't explicitly from the customer is treated as ours.
        from: data.senderType === 'user' ? 'user' : 'admin',
      };
    })
    .filter(m => m.ms != null)
    .sort((a, b) => a.ms - b.ms);

  const notes = notesSnap.docs
    .map((d) => {
      const data = d.data();
      return { ms: toMs(data.createdAt), adminEmail: data.adminEmail || null };
    })
    .filter(n => n.ms != null)
    .sort((a, b) => a.ms - b.ms);

  return { messages, notes };
}

/**
 * useAccountMessageActivity(enabled, accountIds)
 *
 * enabled    — only fetch while the History view is on screen
 * accountIds — account UUIDs to load activity for
 *
 * Returns { activity, loading, error, unavailable, failedIds }
 *   activity    — { [uuid]: { messages: [{ms, from}], notes: [{ms, adminEmail}] } }
 *   error       — some or all reads failed; last-good data is kept
 *   unavailable — EVERY read failed, so the lane should be hidden outright
 *   failedIds   — Set of accounts whose latest read threw; their lane must not
 *                 be drawn as "no messages", which would be a lie
 */
export function useAccountMessageActivity(enabled, accountIds) {
  const [activity, setActivity] = useState({});
  // Deriving `loading` from the id set whose fan-out finished (rather than a
  // flag flipped inside the effect) means the first render after the view
  // opens already reports loading, instead of flashing an empty lane.
  const [loadedKey, setLoadedKey] = useState(null);
  const [error, setError] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [failedIds, setFailedIds] = useState(() => new Set());

  // Stable dependency: the array identity changes on every parent render.
  const idsKey = useMemo(() => (accountIds || []).join(','), [accountIds]);

  // Guards against a slow earlier fan-out overwriting newer results.
  const runIdRef = useRef(0);
  // Must be re-armed on every mount, not just the first: React 18 StrictMode
  // mounts → unmounts → re-mounts in development, so a flag that is only ever
  // cleared would stay false for the component's whole life.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const ids = idsKey ? idsKey.split(',') : [];
    if (ids.length === 0) {
      setActivity({});
      setLoadedKey(idsKey);
      return;
    }

    const runId = ++runIdRef.current;
    (async () => {
      try {
        const results = await mapLimit(ids, CONCURRENCY, async (uuid) => {
          try {
            return [uuid, await fetchActivity(uuid)];
          } catch (e) {
            // One unreadable account must not blank the whole lane.
            console.warn(`useAccountMessageActivity: failed to read ${uuid}:`, e.code || e.message);
            return [uuid, undefined];
          }
        });
        if (!mountedRef.current || runId !== runIdRef.current) return;
        // `undefined` = this account's read threw. Classify before dispatching
        // any state: a useState updater is only required to be pure and React
        // may run it during the render phase, so nothing below may depend on
        // the updater having already run.
        const failed = new Set();
        const fresh = {};
        results.forEach(([uuid, value]) => {
          if (value === undefined) failed.add(uuid);
          else if (value) fresh[uuid] = value;
        });
        setActivity((prev) => {
          const next = { ...fresh };
          failed.forEach((uuid) => { if (prev[uuid]) next[uuid] = prev[uuid]; });
          return next;
        });
        setFailedIds(failed);
        setError(failed.size > 0);
        setUnavailable(failed.size === ids.length);
      } catch (e) {
        console.warn('useAccountMessageActivity: load failed:', e.code || e.message);
        if (mountedRef.current && runId === runIdRef.current) {
          setError(true);
          setUnavailable(true);
        }
      } finally {
        // A failed load is still "no longer loading"; the flags carry the failure.
        if (mountedRef.current && runId === runIdRef.current) setLoadedKey(idsKey);
      }
    })();
  }, [enabled, idsKey]);

  const loading = enabled && idsKey !== loadedKey;

  return { activity, loading, error, unavailable, failedIds };
}
